import Orchestrator from "../Orchestrator.js";
import { CONFIG, columnFromX, columnFromZ, surfaceY } from "./config.js";
import { MIN_Y } from "./terrain/constants.js";
import ChunkManager from "./terrain/ChunkManager.js";
import { loadLayout } from "./terrain/editor/LayoutStore.js";
import shippedLayout from "./terrain/layout.json";
import TerrainData from "./terrain/TerrainData.js";
import TerrainMaterial from "./terrain/TerrainMaterial.js";
import WorkerPool from "./terrain/WorkerPool.js";

export default class Terrain {
    constructor() {
        this.orc = new Orchestrator();
        this.scene = this.orc.scene;
        this.debug = this.orc.debug;

        this.data = new TerrainData();
        this.material = new TerrainMaterial(this.debug);
        this.pool = new WorkerPool();
        this.chunks = new ChunkManager(this.data, this.material, this.pool);
        this.scene.add(this.chunks.group);

        this.ready = false;
        this._generation = 0;
        this.layout = loadLayout(shippedLayout, this.debug.active);
        this.data.layout = this.layout;

        this.orc.emitter.on("terrain-ready", () => {
            this.ready = true;
        });

        this.setLayout(this.layout);
    }

    setLayout(layout) {
        this.layout = layout;
        this.data.layout = layout;
        this.chunks.setSpawn(layout.spawn.x, layout.spawn.z);
        const generation = ++this._generation;
        return this.pool.generate(layout, layout.size).then((result) => {
            if (generation !== this._generation) return;
            if (result.error || !result.heights) {
                console.error(result.error || "Terrain generate failed");
                return;
            }
            this.chunks.applyGenerated(result.heights, result.baseHeights);
        });
    }

    update(camera) {
        this.chunks.update(camera);
    }

    heightAt(i, j) {
        return this.data.heightAt(i, j);
    }

    baseHeightAt(i, j) {
        return this.data.baseHeightAt(i, j);
    }

    depthAt(i, j) {
        return this.data.baseHeightAt(i, j) - this.data.heightAt(i, j);
    }

    surfaceAt(x, z) {
        let i = columnFromX(x);
        let j = columnFromZ(z);
        if (i < 0) i = 0;
        if (j < 0) j = 0;
        if (i >= CONFIG.WORLD_SIZE) i = CONFIG.WORLD_SIZE - 1;
        if (j >= CONFIG.WORLD_SIZE) j = CONFIG.WORLD_SIZE - 1;
        return surfaceY(this.data.heightAt(i, j));
    }

    isSolid(i, j, y) {
        return y >= MIN_Y && y <= this.data.heightAt(i, j);
    }

    dig(i, j) {
        const height = this.data.heightAt(i, j);
        if (height <= MIN_Y) return false;
        const next = height - 1;
        this.data.setColumn(i, j, next);
        this.pool.patch(this.data.index(i, j), next, i, j);
        this.chunks.remeshColumns(i, j);
        return true;
    }

    reset() {
        const dirty = this.data.restoreBase();
        this.pool.broadcastHeights(this.data.heights, CONFIG.WORLD_SIZE);
        this.chunks.remeshAround(dirty);
    }
}
